import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { CategorizedSyncError } from '@/lib/db/rxdb/utils/syncErrorUtils'
import { useRxDB } from '@/lib/db/rxdb/hooks'
import { RXDB_SESSION_STORAGE_KEY, resetRxDB } from '@/lib/db/resetUtils'
import { Button } from '@/components/ui/button'
import useLoginStore from '@/store/LoginStore'
import getWorkspaces from '@/lib/api/features/authentication/api/getWorkspaces'
import { logger } from '@/lib/monitoring/logger'
import { cn } from '@/lib/utils'

/**
 * Checks if the RxDB initialization has been completed in the current session.
 * Uses session storage to persist the flag across page reloads.
 *
 * @returns True if RxDB has been initialized in this session
 */
const getSessionFlag = () =>
  typeof window !== 'undefined' &&
  window.sessionStorage.getItem(RXDB_SESSION_STORAGE_KEY) === 'true'

/**
 * Props for the RxdbSplashScreen component.
 */
type SplashProps = {
  /** Whether RxDB is currently initializing */
  isInitializing: boolean
  /** Whether data is currently syncing */
  isSyncing: boolean
  /** Any error that occurred during initialization */
  error: Error | null
  /** Categorized error information */
  categorizedError: CategorizedSyncError | null
  /** Callback to retry initialization on error */
  onRetry: () => void
  /** Callback to clear local offline data and re-sync from scratch */
  onClearData: () => void
}

/**
 * Loading screen displayed during RxDB initialization and sync.
 * Shows appropriate messages and animations based on current state.
 *
 * @param isInitializing - Whether RxDB is currently initializing
 * @param isSyncing - Whether data is currently syncing
 * @param error - Any error that occurred during initialization
 * @param categorizedError - Categorized error information
 * @param onRetry - Callback to retry initialization on error
 */
const RxdbSplashScreen = ({
  isInitializing,
  isSyncing,
  error,
  categorizedError,
  onRetry,
  onClearData,
}: SplashProps) => {
  // Two-step guard on the destructive "Clear data" action so a stuck user
  // can't wipe local data (including any unsynced changes) with a single
  // stray click.
  const [confirmingClear, setConfirmingClear] = useState(false)

  const statusMessage = useMemo(() => {
    if (error) {
      // Show different messages based on error type
      if (categorizedError?.category === 'auth') {
        return 'Authentication required'
      }
      if (categorizedError?.category === 'client' || categorizedError?.category === 'server') {
        return 'Sync error detected'
      }
      return 'Unable to initialize offline data.'
    }
    if (isInitializing) return 'Welcome to Moly...'
    if (isSyncing) return 'Syncing workspace...'
    return 'Welcome to Moly...'
  }, [error, categorizedError, isInitializing, isSyncing])

  const errorMessage = categorizedError?.message ?? error?.message ?? 'Unknown error'
  const showTechnicalDetails = categorizedError?.statusCode !== undefined

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-center">
      <div
        className={cn(
          'mb-8 flex h-28 w-28 items-center justify-center rounded-full bg-card',
          error ? 'animate-none' : 'animate-[spin_3s_linear_infinite]',
        )}
      >
        <img
          src="/android-chrome-192x192.png"
          alt="Moly logo"
          className="h-14 w-14 object-contain"
        />
      </div>

      <p className="text-base font-semibold text-foreground">{statusMessage}</p>
      {!error ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Setting up your workspace
        </p>
      ) : (
        <div className="mt-4 max-w-md space-y-3">
          <p className="text-sm text-destructive">{errorMessage}</p>
          {showTechnicalDetails && (
            <p className="text-xs text-muted-foreground">
              Error code: {categorizedError?.statusCode}
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Button size="sm" onClick={onRetry}>
              Retry sync
            </Button>
            {categorizedError?.category === 'auth' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  window.location.href = '/auth'
                }}
              >
                Sign in again
              </Button>
            )}
          </div>

          {/* Escape hatch: if a retry keeps failing (e.g. a local schema
              mismatch that a redeploy hasn't fixed yet), let the user wipe the
              cached offline data and re-download it from the server. */}
          <div className="border-t border-border pt-3">
            {confirmingClear ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  This deletes locally cached data and re-downloads it from the
                  server. Any changes not yet synced will be lost.
                </p>
                <div className="flex justify-center gap-2">
                  <Button size="sm" variant="destructive" onClick={onClearData}>
                    Clear and re-sync
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmingClear(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => setConfirmingClear(true)}
              >
                Clear data
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Gate component that shows a loading screen during RxDB initialization.
 *
 * This component ensures the RxDB database is fully initialized and synced
 * before rendering the main application. It displays a loading screen with
 * progress indicators and handles initialization errors gracefully.
 *
 * Features:
 * - Shows loading screen only on first visit per session
 * - Handles initialization errors with retry functionality
 * - Skips loading screen if no workspace context is available
 * - Persists initialization state across page reloads
 * - Only blocks on critical errors (4xx, 5xx) not network/offline errors
 *
 * @param children - The application content to render after initialization
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <RxdbSplashGate>
 *       <MainApplication />
 *     </RxdbSplashGate>
 *   );
 * }
 * ```
 */
/**
 * If RxDB init never reports ready and never errors (e.g. the init promise
 * stalls), the splash would otherwise spin forever. After this long we surface
 * the existing error + Retry UI so the user can recover instead of staring at
 * an indefinite spinner. Generous on purpose — isReady flips as soon as the DB
 * + collections are ready (before full sync), so a real init takes a few
 * seconds; 30s past that means something is genuinely stuck.
 */
const INIT_WATCHDOG_MS = 30_000

export const RxdbSplashGate = ({ children }: { children: ReactNode }) => {
  const { isReady, isInitializing, isSyncing, error, categorizedError, reset } = useRxDB()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const workspaces = useLoginStore((state) => state.workspaces)
  const hasWorkspaceContext = Boolean(workspaceId)
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(() =>
    getSessionFlag(),
  )
  // Trips when init has been stuck on the splash too long with no ready/error
  // signal, so we show a recoverable error instead of an indefinite spinner.
  const [watchdogExpired, setWatchdogExpired] = useState(false)

  // Determine if error is critical and should block the app
  const isCriticalError = error && categorizedError?.isCritical

  // Guard: workspaceId is set but the cached workspaces list doesn't contain it.
  // This can happen when localStorage has a stale workspaceId after the backend
  // removed/renamed the workspace. Without this guard the shell renders a phantom
  // workspace from persisted state while RxDB silently fails to sync.
  const hasMissingActiveWorkspace =
    hasWorkspaceContext &&
    workspaces.length > 0 &&
    !workspaces.some((w) => w.workspaceId === workspaceId)

  const handleRetry = useCallback(async () => {
    // Force splash to block until the next sync completes or fails again.
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(RXDB_SESSION_STORAGE_KEY)
    }
    setHasCompletedInitialLoad(false)
    setWatchdogExpired(false)

    try {
      const resp = await getWorkspaces()
      const store = useLoginStore.getState()
      store.setWorkspaces(resp.data)

      // If the persisted workspaceId is no longer valid, switch to the first
      // available workspace so re-init targets a real env.
      const current = resp.data.find((w) => w.workspaceId === store.workspaceId)
      const fallback = resp.data[0]
      if (!current && fallback) {
        store.switchWorkspace(fallback)
      }
    } catch (err) {
      logger.info('Retry: failed to refresh workspace list, reinitializing with existing state', {
        error: err instanceof Error ? err.message : 'unknown',
        component: 'RxdbSplashGate',
      })
    }

    reset()
  }, [reset])

  useEffect(() => {
    if (!hasWorkspaceContext) {
      return
    }

    if (isReady && !hasCompletedInitialLoad) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(RXDB_SESSION_STORAGE_KEY, 'true')
      }
      setHasCompletedInitialLoad(true)
    }
  }, [hasWorkspaceContext, isReady, hasCompletedInitialLoad])

  // Watchdog: while stuck on the splash with no ready signal and no error, arm a
  // timer; if it fires the init has stalled, so trip a recoverable error rather
  // than leaving the user on an indefinite spinner. Cleared the moment isReady
  // or error resolves (deps below), and reset on retry.
  useEffect(() => {
    if (!hasWorkspaceContext || hasCompletedInitialLoad || isReady || error) {
      return
    }
    const timer = window.setTimeout(
      () => setWatchdogExpired(true),
      INIT_WATCHDOG_MS,
    )
    return () => window.clearTimeout(timer)
  }, [hasWorkspaceContext, hasCompletedInitialLoad, isReady, error])

  // Log non-critical errors but don't block
  useEffect(() => {
    if (error && !categorizedError?.isCritical && hasCompletedInitialLoad) {
      logger.info('Non-critical sync error (continuing in offline mode)', {
        error: error.message,
        category: categorizedError?.category,
        component: 'RxdbSplashGate',
      })
    }
  }, [error, categorizedError, hasCompletedInitialLoad])

  // If the watchdog tripped while still stuck (no real error, never became
  // ready), synthesize a recoverable error so the splash shows the Retry UI.
  const watchdogError =
    watchdogExpired && !error && !isReady
      ? new Error(
          'Setting up your workspace is taking longer than expected. Please retry.',
        )
      : null
  const displayError = error ?? watchdogError

  // Skip splash screen if:
  // 1. No workspace context
  // 2. Already completed initial load AND no critical error AND active workspace resolves
  if (
    !hasWorkspaceContext ||
    (hasCompletedInitialLoad && !isCriticalError && !hasMissingActiveWorkspace)
  ) {
    return <>{children}</>
  }

  return (
    <RxdbSplashScreen
      isInitializing={isInitializing}
      isSyncing={isSyncing}
      error={displayError}
      categorizedError={categorizedError}
      onRetry={() => {
        void handleRetry()
      }}
      onClearData={resetRxDB}
    />
  )
}
